import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';
import { Organization } from 'sr-types/lib/organization/v1/graphql';
import { Person } from 'sr-types/lib/person/v1/graphql';
import { extractOrgDetailsFromSearchQuery } from '../../organization/organization';
import { extractPersonDetailsFromSearchQuery } from '../../person/detailsPage/detailsPageUtilities/personAlternateIds';
import ContactInfoAlert from '../../person/detailsPage/editComponents/ContactInfoAlert';
import { AllPermissions } from '../auth/api';
import { UserContext } from '../auth/UserContext';
import { IMDB_LABEL, STUDIO_SYSTEM_LABEL, STUDIO_SYSTEM_LOCALE_LABEL } from '../constants/alternateIdConstants';
import { ENTITY_CONSTANTS } from '../constants/entityConstants';
import { useI18n } from '../i18n/I18n';
import { PROFILES } from '../nav/apps';
import RenderProfileErrorMessage from './RenderProfileErrorMessage';
import { handleFetchingConflictedEntityDetailsResponse } from './utils/alternateIdFormUtils';

type AlternateIdInfoAlertProps = {
    conflictedEntity: Person | Organization;
    fieldSelected: string;
    onUpdate: () => void;
    setIsSaveDisabled: Dispatch<SetStateAction<boolean>>;
    isPerson?: boolean;
    hasError?: boolean | undefined;
};

/**
 * Displays an informational alert regarding alternate ID conflicts for a person or organization entity.
 *
 * This component handles the logic for fetching and displaying details about a conflicted entity
 * when a user attempts to link an alternate ID (such as IMDb, LinkedIn, or Studio System) that is already associated
 * with another account. It conditionally renders either a profile-specific error message or a contact info alert,
 * depending on the application context and the type of entity involved.
 *
 * @param conflictedEntity - The entity object that has a conflicting alternate ID.
 * @param hasAccount - Indicates if the conflicted entity already has an account.
 * @param fieldSelected - The alternate ID field currently being linked (e.g., IMDb, LinkedIn).
 * @param onUpdate - Callback function to handle update actions.
 * @param setIsSaveDisabled - Function to enable or disable the save action based on conflict state.
 * @param isPerson - Boolean flag indicating if the entity is a person (true) or an organization (false).
 * @default isPerson true
 * @param hasError - Optional key to identify specific error messages.
 *
 * @returns A React element displaying the appropriate alert or error message based on the conflict state.
 */
export default function AlternateIdInfoAlert({
    conflictedEntity,
    fieldSelected,
    onUpdate,
    setIsSaveDisabled,
    isPerson = true,
    hasError = undefined
}: AlternateIdInfoAlertProps) {
    const { application, activeOrganizationAccount } = useContext(UserContext);

    const imdbMatchingProfileErrorText = useI18n('alternateId.validation.link.matching.profile.no.update.imdb');
    const linkedInMatchingProfileErrorText = useI18n(
        'alternateId.validation.link.matching.profile.no.update.linked.in'
    );
    const studioSystemMatchingProfileErrorText = useI18n(
        'alternateId.validation.link.matching.profile.no.update.studio.system'
    );

    const [conflictedEntitySearchDetails, setConflictedEntitySearchDetails] = useState(undefined);
    const [isFetchEntityDetailsError, setIsFetchEntityDetailsError] = useState<boolean>(false);
    const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
    const conflictedEntityHasEditPermission = conflictedEntity?.security?.allowedActions?.includes(AllPermissions.Edit);

    const onSuccessfullyFetchingEntityDetails = useCallback(
        (res) => {
            setIsDataLoading(false);
            const conflictedEntity = isPerson
                ? extractPersonDetailsFromSearchQuery(res?.data?.results?.hits?.items[0])
                : extractOrgDetailsFromSearchQuery(res?.data?.results?.hits?.items[0]);
            setConflictedEntitySearchDetails(conflictedEntity);
        },
        [isPerson]
    );

    useEffect(() => {
        if (Object.keys(conflictedEntity || {}).length) {
            setIsDataLoading(true);

            let email: string | undefined;
            let id: string | undefined;
            if (isPerson && 'contactInfo' in conflictedEntity && conflictedEntity.contactInfo?.email?.address) {
                email = conflictedEntity.contactInfo.email.address;
            } else if (!isPerson && conflictedEntity.identity?.id) {
                id = conflictedEntity.identity.id;
            }
            const entity = isPerson ? ENTITY_CONSTANTS.person : ENTITY_CONSTANTS.organization;
            handleFetchingConflictedEntityDetailsResponse(
                entity,
                email,
                id,
                activeOrganizationAccount,
                onSuccessfullyFetchingEntityDetails,
                () => setIsFetchEntityDetailsError(false)
            );
        } else {
            setConflictedEntitySearchDetails(undefined);
        }
    }, [conflictedEntity, activeOrganizationAccount, isPerson, onSuccessfullyFetchingEntityDetails]);

    const getErrorMessage = () => {
        if (fieldSelected === IMDB_LABEL) return imdbMatchingProfileErrorText;
        if (fieldSelected === STUDIO_SYSTEM_LABEL) return studioSystemMatchingProfileErrorText;
        return linkedInMatchingProfileErrorText;
    };

    if (application.home === PROFILES.home) {
        return (
            <RenderProfileErrorMessage
                hide={!Object.keys(conflictedEntity || {}).length}
                errorMessage={getErrorMessage()}
                setIsSaveDisabled={setIsSaveDisabled}
            />
        );
    }
    return (
        <ContactInfoAlert
            hide={typeof hasError === 'boolean' ? !hasError : !Object.keys(conflictedEntity || {}).length}
            fieldName={fieldSelected === STUDIO_SYSTEM_LABEL ? STUDIO_SYSTEM_LOCALE_LABEL : fieldSelected}
            showUpdateButton={conflictedEntityHasEditPermission && application.home !== PROFILES.home && isPerson}
            onUpdate={onUpdate}
            title={`alternateId.validation.link.matching.${isPerson ? 'people' : 'organization'}.title`}
            description={
                isPerson
                    ? `alternateId.validation.link.matching.people.description${!conflictedEntityHasEditPermission ? '.no.update' : ''}`
                    : 'alternateId.validation.link.matching.organization.description.no.update'
            }
            conflictedPerson={conflictedEntitySearchDetails}
            isPersonLoading={isDataLoading}
            isFetchPersonDetailsError={isFetchEntityDetailsError}
        />
    );
}
